import { ApolloClient } from '@apollo/client';

import { Country } from '@skippay/core/@types/country';
import { DEFAULT_COUNTRY, getCurrency } from '@skippay/core/hooks/useUserCountryRelatedInfo';
import { Attrs } from '@skippay/core/utils/objectUtils';

import { JarabotContactAttributes, jarabotClient } from '../jarabotClient';
import { IS_NON_PROD, getJarabotDelay } from '../utils';
import { ContextAdapter, DerivedSource, DomainSource, isDerivedSource } from './adapters/types';
import { CustomerContext, CustomerContextDomain } from './types';
import { buildDomainAttrs } from './whitelist';

const ENABLE_KEY = '__jarabot_customer_context_enabled';
const STAGGER_KEY = '__jarabot_customer_context_stagger';
const DEBOUNCE_KEY = '__jarabot_customer_context_debounce';
const DEFAULT_STAGGER_MS = 100;
const DEFAULT_DEBOUNCE_MS = 50;

export interface JarabotIdentity {
	userId: string;
	name?: string;
	email?: string;
	phone?: string;
	countryCode?: string;
	kycIsOutdated?: boolean;
	amlLegalReviewRequested?: boolean;
}

export type JarabotSurface = 'selfcare' | 'gate';

interface StartParams {
	// eslint-disable-next-line @typescript-eslint/ban-types
	apolloClient: ApolloClient<Record<string, unknown>>;
	country?: Country;
	surface: JarabotSurface;
	identity?: JarabotIdentity;
	adapter: ContextAdapter;
}

interface Subscription {
	unsubscribe: () => void;
}

let started = false;
let active = false;
let pending: StartParams | null = null;
let activationHandler: (() => void) | null = null;
let model: CustomerContext = {};
let subscriptions: Subscription[] = [];
let derivedSources: DerivedSource[] = [];
let staggerTimers: number[] = [];
const debounceTimers: Partial<Record<CustomerContextDomain, number>> = {};
let pushedKeys: Partial<Record<CustomerContextDomain, string[]>> = {};

function isEnabled(country?: Country): boolean {
	try {
		return (
			IS_NON_PROD &&
			country === 'CZ' &&
			localStorage.getItem(ENABLE_KEY) !== '0' &&
			!process.env.MOB_APP_BUILD
		);
	} catch {
		return false;
	}
}

function setAttribute(attrs: Attrs): void {
	if (Object.keys(attrs).length === 0) return;
	try {
		jarabotClient.setCustomAttributes(attrs);
	} catch {
		// silently swallow, no error needed
	}
}

function flushDomain(domain: CustomerContextDomain): void {
	try {
		const attrs = buildDomainAttrs(domain, model);
		(pushedKeys[domain] ?? []).forEach((key) => {
			if (key in attrs) return;
			try {
				jarabotClient.deleteCustomAttribute(key);
			} catch {
				// silently swallow, no error needed
			}
		});
		pushedKeys[domain] = Object.keys(attrs);
		setAttribute(attrs);
	} catch {
		// silently swallow, no error needed
	}
}

function scheduleFlush(domain: CustomerContextDomain): void {
	const existing = debounceTimers[domain];
	if (existing) clearTimeout(existing);
	const wait = getJarabotDelay(DEBOUNCE_KEY, DEFAULT_DEBOUNCE_MS);
	debounceTimers[domain] = window.setTimeout(() => {
		delete debounceTimers[domain];
		flushDomain(domain);
	}, wait);
}

function runDerived(): void {
	derivedSources.forEach((source) => {
		try {
			const next = source.toModel(model);
			const changed = source.domains.filter(
				(domain) => JSON.stringify(model[domain]) !== JSON.stringify(next[domain])
			);
			model = { ...model, ...next };
			changed.forEach(scheduleFlush);
		} catch {
			// silently swallow, no error needed
		}
	});
}

function subscribe(
	apolloClient: ApolloClient<Record<string, unknown>>,
	source: DomainSource<Record<string, unknown>>
): void {
	try {
		const observable = apolloClient.watchQuery<Record<string, unknown>>({
			query: source.query,
			variables: source.getVariables ? source.getVariables() : source.variables,
			fetchPolicy: 'cache-first',
			nextFetchPolicy: 'cache-only',
			returnPartialData: true,
		});
		const sub = observable.subscribe({
			next: ({ data }) => {
				if (!started || !data) return;
				try {
					model = { ...model, ...source.toModel(data, model) };
					source.domains.forEach(scheduleFlush);
				} catch {
					// silently swallow, no error needed
				}
				runDerived();
			},
		});
		subscriptions.push(sub);
	} catch {
		// silently swallow, no error needed
	}
}

function pushIdentity(identity: JarabotIdentity, country: Country): void {
	try {
		jarabotClient.setUser(identity.userId, country, {
			name: identity.name,
			email: identity.email,
			phone_number: identity.phone,
			country_code: identity.countryCode,
		});
		const contactAttrs: JarabotContactAttributes = {
			kyc_verified: identity.kycIsOutdated === false,
			'customer.amlLegalReview.isRequested': identity.amlLegalReviewRequested,
		};
		jarabotClient.setCustomAttributes(contactAttrs);
	} catch {
		// silently swallow, no error needed
	}
}

function activate(): void {
	if (active || !pending) return;
	active = true;
	const { apolloClient, country, surface, identity, adapter } = pending;

	const resolvedCountry = country ?? DEFAULT_COUNTRY;

	if (identity) pushIdentity(identity, resolvedCountry);
	setAttribute({ currency: getCurrency(resolvedCountry), surface });

	derivedSources = adapter.filter(isDerivedSource);

	const stagger = getJarabotDelay(STAGGER_KEY, DEFAULT_STAGGER_MS);
	adapter.forEach((source, index) => {
		if (isDerivedSource(source)) return;
		const timer = window.setTimeout(() => subscribe(apolloClient, source), index * stagger);
		staggerTimers.push(timer);
	});
}

export const jarabotCustomerContext = {
	start(params: StartParams): boolean {
		if (started || !isEnabled(params.country) || !params.apolloClient) return false;

		started = true;
		pending = params;
		activationHandler = activate;
		window.addEventListener('jarabot:ready', activate);

		if (window.$jarabot?.hasLoaded) activate();
		return true;
	},

	refreshDerived(): boolean {
		if (!started) return false;
		if (active) runDerived();
		return true;
	},

	stop(): void {
		if (activationHandler) {
			window.removeEventListener('jarabot:ready', activate);
			activationHandler = null;
		}
		staggerTimers.forEach(clearTimeout);
		staggerTimers = [];
		(Object.keys(debounceTimers) as CustomerContextDomain[]).forEach((domain) => {
			const timer = debounceTimers[domain];
			if (timer) clearTimeout(timer);
			delete debounceTimers[domain];
		});
		subscriptions.forEach((sub) => {
			try {
				sub.unsubscribe();
			} catch {
				// ignore
			}
		});
		subscriptions = [];
		derivedSources = [];
		model = {};
		pushedKeys = {};
		pending = null;
		active = false;
		started = false;
	},
};
